import {
  ArrowForwardOutlined,
  CheckCircleOutlined,
  DocumentScannerOutlined,
  HubOutlined,
  KeyOutlined,
  ManageAccountsOutlined,
  PolicyOutlined,
  TaskAltOutlined,
  TouchAppOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { Box, Container, Stack, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import React from 'react';

const MotionBox = motion.create(Box);

const steps = [
  {
    index: '01',
    icon: <HubOutlined />,
    title: '理解',
    subtitle: '读取业务上下文',
    description: '从项目、会议、文档和组织数据中确认目标、权限与可用资料。',
  },
  {
    index: '02',
    icon: <ManageAccountsOutlined />,
    title: '执行',
    subtitle: '拆解并调用工具',
    description: '形成行动计划，调用日程、待办、审批、检索与生成能力推进任务。',
  },
  {
    index: '03',
    icon: <TouchAppOutlined />,
    title: '确认',
    subtitle: '人工复核后落地',
    description: '关键动作等待负责人确认，结果连同依据和调用记录一起归档。',
  },
];

const scenarios = [
  {
    icon: <DocumentScannerOutlined />,
    title: '项目周报',
    description: '汇总本周会议、文件与待办进展，生成可复核的项目周报初稿。',
    meta: '资料整理 → 周报草稿',
  },
  {
    icon: <TaskAltOutlined />,
    title: '行动跟进',
    description: '从会议结论识别负责人和截止时间，形成可追踪的业务待办。',
    meta: '会议结论 → 待办',
  },
  {
    icon: <PolicyOutlined />,
    title: '流程协办',
    description: '理解审批与制度要求，准备材料、检查缺项并提醒下一步。',
    meta: '制度理解 → 材料检查',
  },
];

const governance = [
  {
    icon: <KeyOutlined />,
    title: '权限先于任务',
    text: '只读取当前用户与业务空间有权访问的数据。',
  },
  {
    icon: <VisibilityOutlined />,
    title: '过程可追溯',
    text: '保留任务拆解、工具调用、来源引用与确认记录。',
  },
  {
    icon: <CheckCircleOutlined />,
    title: '结果可复核',
    text: '自动生成内容明确标记依据，重要结论回到业务系统确认。',
  },
];

const Workflow: React.FC = () => {
  const reduceMotion = useReducedMotion();

  return (
    <Box
      id="workflow"
      component="section"
      sx={{
        py: { xs: 9, md: 13 },
        color: '#edf5ef',
        backgroundColor: 'rgb(var(--footer-rgb))',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          opacity: 0.11,
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,.7) 1px, transparent 0)',
          backgroundSize: '26px 26px',
          maskImage: 'linear-gradient(to right, transparent, black 35%, black 70%, transparent)',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <MotionBox
          {...(reduceMotion
            ? {}
            : {
                initial: { opacity: 1, y: 22 },
                whileInView: { opacity: 1, y: 0 },
                transition: { duration: 0.55 },
                viewport: { once: true, amount: 0.35 },
              })}
          sx={{ maxWidth: 760, mb: { xs: 5, md: 7 } }}
        >
          <Typography
            sx={{
              color: 'rgb(var(--accent-rgb))',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.72rem',
              letterSpacing: '0.14em',
              mb: 1.5,
            }}
          >
            HOW AGENTS GET WORK DONE
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '3.35rem' },
              fontWeight: 800,
              letterSpacing: '-0.045em',
              lineHeight: 1.1,
            }}
          >
            从一句需求，
            <Box component="span" sx={{ color: 'rgb(var(--brand-rgb))' }}>
              到可核验的结果。
            </Box>
          </Typography>
          <Typography sx={{ mt: 2.2, color: 'rgba(237, 245, 239, 0.64)', lineHeight: 1.85, maxWidth: 680 }}>
            Agent 的价值不只在生成内容，更在于理解业务约束、推动流程前进，
            并让每一步都能被人检查和接管。
          </Typography>
        </MotionBox>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: { xs: 1.5, md: 2 },
          }}
        >
          {steps.map((step, index) => (
            <MotionBox
              key={step.index}
              {...(reduceMotion
                ? {}
                : {
                    initial: { opacity: 1, y: 18 },
                    whileInView: { opacity: 1, y: 0 },
                    transition: { duration: 0.42, delay: index * 0.08 },
                    viewport: { once: true, amount: 0.3 },
                  })}
              sx={{
                position: 'relative',
                minHeight: 285,
                p: { xs: 2.5, md: 3 },
                borderRadius: '24px',
                backgroundColor: 'rgba(255,255,255,.055)',
                border: '1px solid rgba(255,255,255,.10)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '15px',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'rgb(var(--accent-rgb))',
                    backgroundColor: 'rgba(var(--accent-rgb), .11)',
                    fontSize: 25,
                  }}
                >
                  {step.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    color: 'rgba(255,255,255,.24)',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                  }}
                >
                  {step.index}
                </Typography>
              </Box>
              <Typography sx={{ mt: 3, fontSize: '1.42rem', fontWeight: 750 }}>
                {step.title}
                <Box component="span" sx={{ ml: 1, fontSize: '0.8rem', fontWeight: 500, opacity: 0.48 }}>
                  {step.subtitle}
                </Box>
              </Typography>
              <Typography sx={{ mt: 1.2, color: 'rgba(237,245,239,.62)', lineHeight: 1.8, fontSize: '0.9rem' }}>
                {step.description}
              </Typography>
              {index < steps.length - 1 && (
                <ArrowForwardOutlined
                  aria-hidden="true"
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'absolute',
                    right: -21,
                    top: '50%',
                    zIndex: 2,
                    color: 'rgb(var(--accent-rgb))',
                    backgroundColor: 'rgb(var(--footer-rgb))',
                    p: 0.5,
                    borderRadius: '50%',
                  }}
                />
              )}
            </MotionBox>
          ))}
        </Box>

        <Box
          sx={{
            mt: { xs: 7, md: 9 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, .78fr) minmax(0, 1.22fr)' },
            gap: { xs: 4, lg: 7 },
            alignItems: 'start',
          }}
        >
          <Box>
            <Typography
              sx={{
                color: 'rgb(var(--accent-rgb))',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.68rem',
                letterSpacing: '0.13em',
                mb: 1.2,
              }}
            >
              BUILT FOR ENGINEERING WORK
            </Typography>
            <Typography sx={{ fontSize: { xs: '1.55rem', md: '2.1rem' }, fontWeight: 750, lineHeight: 1.3 }}>
              围绕项目现场，
              <br />
              把高频办公动作交给 Agent。
            </Typography>
          </Box>

          <Stack spacing={1.2}>
            {scenarios.map((scenario) => (
              <Box
                key={scenario.title}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '40px 1fr', sm: '44px minmax(0, .7fr) minmax(0, 1fr)' },
                  gap: { xs: 1.4, sm: 2 },
                  alignItems: 'center',
                  p: { xs: 1.8, sm: 2 },
                  borderRadius: '18px',
                  border: '1px solid rgba(255,255,255,.09)',
                  backgroundColor: 'rgba(255,255,255,.045)',
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'rgb(var(--brand-rgb))',
                    backgroundColor: 'rgba(var(--brand-rgb), .10)',
                    gridRow: { xs: 'span 2', sm: 'auto' },
                  }}
                >
                  {scenario.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{scenario.title}</Typography>
                  <Typography
                    sx={{
                      display: { xs: 'block', sm: 'none' },
                      mt: 0.4,
                      color: 'rgba(237,245,239,.58)',
                      fontSize: '0.8rem',
                      lineHeight: 1.65,
                    }}
                  >
                    {scenario.description}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    color: 'rgba(237,245,239,.6)',
                    fontSize: '0.84rem',
                    lineHeight: 1.7,
                  }}
                >
                  {scenario.description}
                </Typography>
                <Typography
                  sx={{
                    gridColumn: { xs: '2', sm: '2 / 4' },
                    mt: { xs: 0.4, sm: 0.5 },
                    color: 'rgb(var(--accent-rgb))',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.67rem',
                    letterSpacing: '0.04em',
                  }}
                >
                  {scenario.meta}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box
          id="security"
          sx={{
            mt: { xs: 8, md: 10 },
            pt: { xs: 5, md: 6 },
            borderTop: '1px solid rgba(255,255,255,.11)',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, .8fr) minmax(0, 1.2fr)' },
              gap: { xs: 3, md: 6 },
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: 'rgb(var(--accent-rgb))',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.14em',
                  mb: 1.3,
                }}
              >
                CONTROL & TRUST
              </Typography>
              <Typography sx={{ fontSize: { xs: '1.65rem', md: '2.25rem' }, fontWeight: 760, lineHeight: 1.25 }}>
                自动化有边界，
                <br />
                责任始终清晰。
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 1.2,
              }}
            >
              {governance.map((item) => (
                <Box key={item.title} sx={{ p: 1.6, borderRadius: '16px', backgroundColor: 'rgba(255,255,255,.045)' }}>
                  <Box sx={{ color: 'rgb(var(--accent-rgb))', display: 'flex', mb: 1 }}>{item.icon}</Box>
                  <Typography sx={{ fontWeight: 650, fontSize: '0.9rem' }}>{item.title}</Typography>
                  <Typography sx={{ mt: 0.6, color: 'rgba(237,245,239,.56)', fontSize: '0.76rem', lineHeight: 1.65 }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Workflow;
